// Selskapsinfo — command center (amber-glass). Route: /selskapsinfo/:orgnr?
// IA følger den godkjente mockupen (mockup-toff.html): topbar → hero → KPI-strip →
// eksponering + dekning → styre/roller → pengestrøm → finansiell utvikling.
// Pengestrøm-kartet er en placeholder-kontrakt (kart-agenten leverer den ekte).
import { useNavigate, useParams } from "react-router-dom";

import "./selskapsinfo.css";
import { useBrreg, useEksponering, usePengestrom } from "./api/hooks";
import { useKonsern } from "./api/konsernApi";
import { TopBar } from "./components/TopBar";
import { HeroCard } from "./components/HeroCard";
import { KpiTiles } from "./components/KpiTiles";
import { ExposureCard } from "./components/ExposureCard";
import { CoverageRing } from "./components/CoverageRing";
import { RollerCard } from "./components/RollerCard";
import { KunngjoringerCard } from "./components/KunngjoringerCard";
import { PengestromMap } from "./components/PengestromMap";
import { FinanceChart } from "./components/FinanceChart";

export default function SelskapsinfoPage() {
  const navigate = useNavigate();
  const { orgnr = "" } = useParams<{ orgnr?: string }>();

  const brreg = useBrreg(orgnr);
  const eksponering = useEksponering(orgnr);
  const pengestrom = usePengestrom(orgnr);
  const konsern = useKonsern(orgnr || null);

  const activeName = brreg.data?.enhet?.navn ?? null;

  return (
    <div className="selskapsinfo min-h-screen">
      <div className="mx-auto max-w-[1080px] px-7 pb-20 pt-10">
        <TopBar
          activeName={activeName}
          aktivOrg={orgnr}
          noder={konsern.noder}
          onSelect={(o) => navigate(`/selskapsinfo/${o}`)}
        />

        {!orgnr ? (
          <div className="glass px-[30px] py-16 text-center">
            <h1 className="m-0 text-[26px] font-bold tracking-[-0.02em] text-[var(--ink)]">
              Slå opp et selskap
            </h1>
            <p className="mt-3 text-[13.5px] text-[var(--sub)]">
              Søk på org.nr eller navn i feltet over for å se eksponering, regnskap og
              pengestrøm.
            </p>
          </div>
        ) : (
          <>
            <HeroCard enhet={brreg.data?.enhet} isLoading={brreg.isLoading} />

            <KpiTiles
              eksponering={eksponering.data}
              regnskap={brreg.data?.regnskap}
              isLoading={eksponering.isLoading || brreg.isLoading}
            />

            <div className="mb-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-[1fr_300px]">
              <ExposureCard data={eksponering.data} isLoading={eksponering.isLoading} />
              <CoverageRing data={eksponering.data} isLoading={eksponering.isLoading} />
            </div>

            <RollerCard roller={brreg.data?.roller} isLoading={brreg.isLoading} />

            <KunngjoringerCard orgnr={orgnr} />

            <div className="glass mb-[18px] px-6 py-[22px]">
              <h2 className="m-0 mb-1 text-[13px] font-semibold text-[var(--sub)]">
                Pengestrøm · hvor kapitalen kom fra → prosjektet
              </h2>
              <p className="mb-3.5 mt-0 text-[11.5px] text-[var(--faint)]">
                Aggregert investor-geografi (loan_orders → poststed) → prosjektadresse.
              </p>
              <PengestromMap data={pengestrom.data} isLoading={pengestrom.isLoading} />
            </div>

            <FinanceChart regnskap={brreg.data?.regnskap} isLoading={brreg.isLoading} />
          </>
        )}
      </div>
    </div>
  );
}
